import { useState } from "react";

interface RangeSliderProps {
  min: number;
  max: number;
  step: number;
  lo: number;
  hi: number;
  onChange: (lo: number, hi: number) => void;
  format?: (n: number) => string;
  /** Optional baseline marker — draws a vertical tick at this value
   *  on the track. Used to anchor the user's mental model in
   *  Relative mode (0 = "matches neighbours"). */
  baseline?: number;
}

/**
 * Dual-handle range slider on a single track.
 *
 * Two overlapping `<input type="range">` elements share one styled
 * track — the inputs are `pointer-events: none` so the inert track
 * doesn't capture clicks; only the thumbs (made auto via pseudo-
 * elements) receive drag events. The active band between the two
 * thumbs is a styled div sitting between track and inputs.
 *
 * z-index of the dragging thumb is raised on focus so a thumb that
 * sits on top of its sibling can still be picked up from the other
 * side.
 */
export function RangeSlider({
  min,
  max,
  step,
  lo,
  hi,
  onChange,
  format,
  baseline,
}: RangeSliderProps) {
  const fmt = format ?? ((n: number) => n.toFixed(1));

  // Which handle was last interacted with — drives z-index so a
  // pinned-together pair still drags cleanly from either side.
  const [active, setActive] = useState<"lo" | "hi">("lo");

  const span = max - min;
  const loPct = span > 0 ? Math.max(0, ((lo - min) / span) * 100) : 0;
  const hiPct = span > 0 ? Math.min(100, ((hi - min) / span) * 100) : 100;
  const baselinePct =
    baseline !== undefined && span > 0 && baseline >= min && baseline <= max
      ? ((baseline - min) / span) * 100
      : null;

  return (
    <div>
      <div className="relative" style={{ height: "20px" }}>
        {/* Inert track */}
        <div
          className="absolute left-0 right-0 rounded-full"
          style={{
            top: "50%",
            transform: "translateY(-50%)",
            height: "4px",
            background: "var(--color-mist)",
          }}
        />
        {/* Active band between thumbs */}
        <div
          className="absolute rounded-full"
          style={{
            top: "50%",
            transform: "translateY(-50%)",
            left: `${loPct}%`,
            right: `${100 - hiPct}%`,
            height: "4px",
            background: "var(--color-copper)",
          }}
        />
        {/* Baseline tick — vertical line marking, e.g., 0 in Relative
         *  mode. Drawn behind the thumbs so dragging passes over it. */}
        {baselinePct !== null && (
          <div
            className="absolute pointer-events-none"
            style={{
              top: "50%",
              left: `${baselinePct}%`,
              width: "1px",
              height: "12px",
              background: "var(--color-slate)",
              transform: "translate(-50%, -50%)",
              opacity: 0.5,
            }}
            aria-hidden
          />
        )}
        {/* Lo input */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={lo}
          aria-label="Minimum"
          onMouseDown={() => setActive("lo")}
          onTouchStart={() => setActive("lo")}
          onFocus={() => setActive("lo")}
          onChange={(e) => {
            const next = Number(e.target.value);
            onChange(Math.min(next, hi - step), hi);
          }}
          className="pfd-range absolute inset-0 w-full"
          style={{ zIndex: active === "lo" ? 3 : 2, height: "20px" }}
        />
        {/* Hi input */}
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={hi}
          aria-label="Maximum"
          onMouseDown={() => setActive("hi")}
          onTouchStart={() => setActive("hi")}
          onFocus={() => setActive("hi")}
          onChange={(e) => {
            const next = Number(e.target.value);
            onChange(lo, Math.max(next, lo + step));
          }}
          className="pfd-range absolute inset-0 w-full"
          style={{ zIndex: active === "hi" ? 3 : 2, height: "20px" }}
        />
      </div>
      {/* Slider bounds — labels at the track ends so the user knows
       *  there's room to drag farther than the current band. */}
      <div
        className="flex justify-between mt-1.5"
        style={{
          fontSize: "9.5px",
          color: "var(--color-stone)",
          fontVariantNumeric: "tabular-nums",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        <span>{fmt(min)}</span>
        <span>{fmt(max)}</span>
      </div>
    </div>
  );
}
